/**
 * JourneyDayPage — orchestrates the full Absorption Flow for a single day.
 *
 * Steps (Absorption v5):
 *   1. arrival   — calm breathing reset (28s, button-free, 3-line cycle)
 *   2. imprint   — Conscious Inputting: full image+answer cards, no blur,
 *                  scrollable list (first conscious exposure)
 *   3. flash     — flash cards with blur/reveal (active recall, "the quiz")
 *   4. precision — precision drill cards (stats, no illustration)
 *   5. scenario  — scenario cards with blur/reveal
 *   6. expand    — peripheral-vision widening reset
 *   7. rapid     — ZOX-style passive 1-card-per-flash pass (same visuals)
 *   8. immersion — 3-min RLI + subliminals
 *   9. seal      — 13-second soft-landing close
 *  10. milestone — Day 7/14/21 only: gold halo + Share a Moment
 *  11. night     — sleep reinforcement (Lantern Room music + affirmations)
 *
 * URL: /journeys/:slug/day/:day
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ResetScreen from "../../components/journeys/ResetScreen";
import CardDeck from "../../components/journeys/CardDeck";
import ConsciousImprint from "../../components/journeys/ConsciousImprint";
import RapidInstallation from "../../components/journeys/RapidInstallation";
import ContinuousExpandFlow from "../../components/journeys/ContinuousExpandFlow";
import ImmersionPlayer from "../../components/journeys/ImmersionPlayer";
import CompletionSeal from "../../components/journeys/CompletionSeal";
import ShareMoment, {
  isMilestoneDay,
  hasSeenMilestone,
} from "../../components/journeys/ShareMoment";
import AttentionGuardConsent, {
  isAttentionGuardDecided,
} from "../../components/journeys/AttentionGuardConsent";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_LABEL,
} from "../../components/journeys/journeyTheme";
import { Loader2, ArrowLeft, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem("reclaim_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STEPS = ["arrival", "imprint", "flash", "precision", "scenario", "expand", "rapid", "immersion", "seal", "milestone", "night"];

/* Derive Layer-2 subliminal content from the day's content.
 * Returns { keywords, phrases }:
 *   keywords = single-word distinctive tokens (60% of mix)
 *   phrases  = 2–4 word micro structures (25% of mix)
 * Short, distinctive only — peripheral flashes must be felt, not read. */
function deriveSubliminals(content) {
  if (!content) return { keywords: [], phrases: [] };
  const kbank = new Set();
  const pbank = new Set();
  const addK = (s) => {
    if (!s) return;
    const t = String(s).trim().toLowerCase();
    if (t.length >= 2 && t.length <= 14) kbank.add(t);
  };
  const addP = (s) => {
    if (!s) return;
    const t = String(s).trim().toLowerCase();
    const w = t.split(/\s+/).length;
    if (t.length >= 6 && t.length <= 26 && w >= 2 && w <= 4) pbank.add(t);
  };
  // Conscious immersion words → keywords
  (content.immersion?.words || []).forEach((w) => {
    if ((w.text || "").split(/\s+/).length === 1) addK(w.text);
    else addP(w.text);
  });
  // Card backs — extract numeric tokens (kw) + first short clause (phrase)
  const allCards = [
    ...(content.cards?.flash || []),
    ...(content.cards?.precision || []),
    ...(content.cards?.scenario || []),
  ];
  allCards.forEach((c) => {
    const back = (c.back || "").toLowerCase();
    // numeric tokens (kw): "30 mph", "9 metres", "two seconds"
    const num = back.match(/\b\d{1,3}\s?(mph|metres?|seconds?|m\b|s\b)\b/g);
    (num || []).forEach(addK);
    // first clause → phrase
    const lead = back.split(/[—.,;:]/)[0]?.trim();
    addP(lead);
    // single distinctive word at start of front
    const frontFirst = (c.front || "").split(/\s+/)[0]?.toLowerCase();
    addK(frontFirst);
  });
  // Lesson title
  addP(content.lesson?.title?.toLowerCase());
  return {
    keywords: Array.from(kbank).slice(0, 18),
    phrases: Array.from(pbank).slice(0, 10),
  };
}

export default function JourneyDayPage() {
  const { slug, day } = useParams();
  const dayNumber = parseInt(day, 10);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [content, setContent] = useState(null);
  // Resume at the step recorded in sessionStorage if the user is returning
  // from the standalone Reset → Expand page (we navigate them out of the
  // app's day flow into /networker/resets/expand and back).
  const [stepIndex, setStepIndex] = useState(() => {
    if (searchParams.get("resetComplete") === "true") {
      const resumeIdx = STEPS.indexOf("rapid");
      return resumeIdx >= 0 ? resumeIdx : 0;
    }
    return 0;
  });
  const [error, setError] = useState(null);
  // Forces re-read of localStorage attention-guard flags after consent.
  const [consentBump, setConsentBump] = useState(0);
  // Guard so the expand-step navigation only fires once per visit.
  const expandLaunchedRef = useRef(false);
  // Continuous Expand → Rapid → Immersion audio (single voice take). When
  // available we replace the legacy 3-step flow with one continuous experience.
  // Probe with a HEAD request on mount; null until checked, then string|false.
  const [continuousExpandAudio, setContinuousExpandAudio] = useState(null);

  useEffect(() => {
    const url = `${API}/api/audio/voice/expand_continuous_v1.mp3`;
    fetch(url, { method: "HEAD" })
      .then((r) => setContinuousExpandAudio(r.ok ? url : false))
      .catch(() => setContinuousExpandAudio(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/journeys/${slug}/days/${dayNumber}`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => { if (!cancelled) setContent(data); })
      .catch((e) => setError(typeof e === "number" ? `Day ${dayNumber} not found` : "Could not load"));
    return () => { cancelled = true; };
  }, [slug, dayNumber]);

  const markStep = async (stepName) => {
    try {
      await fetch(`${API}/api/journeys/${slug}/progress/step`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ day: dayNumber, step: stepName }),
      });
    } catch {}
  };

  const advance = async (stepName) => {
    if (stepName) await markStep(stepName);
    setStepIndex((i) => Math.min(i + 1, STEPS.length));
  };

  // Clean the resetComplete query flag once we've acted on it so a hard
  // refresh later doesn't keep skipping the user past arrival.
  useEffect(() => {
    if (searchParams.get("resetComplete") === "true") {
      const next = new URLSearchParams(searchParams);
      next.delete("resetComplete");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // When the user reaches the 'expand' step, EITHER:
  //   (A) Continuous audio file is available → render `ContinuousExpandFlow`
  //       inline (covers expand + rapid + first frame of immersion).
  //   (B) Otherwise → send them out to the existing site Reset → Expand
  //       page (Michael's voice). Journey resumes at 'rapid' on return.
  useEffect(() => {
    if (!content) return;
    if (STEPS[stepIndex] !== "expand") return;
    if (continuousExpandAudio === null) return; // still probing
    if (continuousExpandAudio) return; // new flow handles it inline
    if (expandLaunchedRef.current) return;
    expandLaunchedRef.current = true;
    try {
      sessionStorage.setItem(
        "journeyResetReturn",
        JSON.stringify({ returnPath: `/journeys/${slug}/day/${dayNumber}` })
      );
    } catch {}
    markStep("expand");
    navigate(`/networker/resets/expand?fromJourney=true`);
  }, [stepIndex, content, slug, dayNumber, navigate]);

  // Auto-skip empty steps so skeleton days flow without stalling.
  useEffect(() => {
    if (!content) return;
    const step = STEPS[stepIndex];
    const totalCards =
      (content.cards?.flash?.length || 0) +
      (content.cards?.precision?.length || 0) +
      (content.cards?.scenario?.length || 0);
    const empty =
      (step === "imprint"   && totalCards === 0) ||
      (step === "flash"     && !(content.cards?.flash?.length))     ||
      (step === "precision" && !(content.cards?.precision?.length)) ||
      (step === "scenario"  && !(content.cards?.scenario?.length))  ||
      (step === "rapid"     && totalCards === 0) ||
      (step === "milestone" &&
        (!isMilestoneDay(dayNumber) || hasSeenMilestone(slug, dayNumber)));
    if (empty) {
      const t = setTimeout(() => setStepIndex((i) => i + 1), 50);
      return () => clearTimeout(t);
    }
  }, [content, stepIndex, dayNumber, slug]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: P.bgWash }}>
        <div>
          <p className="text-[14px]" style={{ color: P.textSecondary, fontFamily: JOURNEY_FONT_HEAD, fontStyle: "italic" }}>
            {error}
          </p>
          <button
            onClick={() => navigate(`/journeys`)}
            className="mt-4 text-[11px] tracking-[0.3em] uppercase"
            style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL }}
          >
            ← back
          </button>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: P.bgWash }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: P.accentBlue }} />
      </div>
    );
  }

  const currentStep = STEPS[stepIndex];

  // Show the one-time attention-guard consent modal right before the user
  // enters the rapid-installation step (which is when the camera would
  // actually be used). Decision persists in localStorage forever.
  // `consentBump` forces re-evaluation of localStorage when changed.
  // eslint-disable-next-line no-unused-vars
  const _bump = consentBump;
  const showAttentionConsent =
    currentStep === "rapid" && !isAttentionGuardDecided();

  return (
    <div data-testid="journey-day-page">
      {showAttentionConsent && (
        <AttentionGuardConsent
          onDecided={() => setConsentBump((n) => n + 1)}
        />
      )}
      <AnimatePresence mode="wait">
        {currentStep === "arrival" && (
          <motion.div
            key="arrival"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ResetScreen
              guidance={content.reset?.guidance}
              durationSeconds={content.reset?.duration_seconds || 28}
              onContinue={() => advance("arrival")}
            />
          </motion.div>
        )}

        {currentStep === "imprint" && (
          <motion.div key="imprint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
            <ConsciousImprint
              content={content}
              onContinue={() => advance("imprint")}
            />
          </motion.div>
        )}

        {currentStep === "flash" && content.cards?.flash?.length > 0 && (
          <motion.div key="flash" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <CardDeck variant="flash" cards={content.cards.flash} onComplete={() => advance("flash")} />
          </motion.div>
        )}

        {currentStep === "precision" && content.cards?.precision?.length > 0 && (
          <motion.div key="precision" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <CardDeck variant="precision" cards={content.cards.precision} onComplete={() => advance("precision")} />
          </motion.div>
        )}

        {currentStep === "scenario" && content.cards?.scenario?.length > 0 && (
          <motion.div key="scenario" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <CardDeck variant="scenario" cards={content.cards.scenario} onComplete={() => advance("scenario")} />
          </motion.div>
        )}

        {currentStep === "expand" && (
          continuousExpandAudio ? (
            // ─── New flow: one continuous voice track drives expand →
            // rapid → handoff. Skips ahead two steps (past 'rapid') so
            // we land directly on 'immersion' when audio finishes.
            <motion.div
              key="continuous-expand"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <ContinuousExpandFlow
                audioUrl={continuousExpandAudio}
                content={content}
                slug={slug}
                day={dayNumber}
                onComplete={async () => {
                  await markStep("expand");
                  await markStep("rapid");
                  setStepIndex(STEPS.indexOf("immersion"));
                }}
              />
            </motion.div>
          ) : (
            // ─── Legacy: brief loader while we navigate to the standalone
            // Reset → Expand page (handled by the useEffect above).
            <motion.div
              key="expand"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="min-h-screen flex items-center justify-center"
              style={{ background: P.bgWash }}
              data-testid="expand-redirecting"
            >
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: P.accentBlue }} />
            </motion.div>
          )
        )}

        {currentStep === "rapid" && (
          <motion.div key="rapid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
            <RapidInstallation
              content={content}
              slug={slug}
              day={dayNumber}
              onContinue={() => advance("rapid")}
            />
          </motion.div>
        )}

        {currentStep === "immersion" && content.immersion && (() => {
          const subs = content.immersion.subliminals || deriveSubliminals(content);
          // Allow seed override in either {keywords, phrases} shape OR plain array
          const keywords = Array.isArray(subs)
            ? subs
            : subs.keywords || [];
          const phrases = Array.isArray(subs) ? [] : subs.phrases || [];
          return (
            <motion.div key="immersion" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ImmersionPlayer
                durationSeconds={content.immersion.duration_seconds || 180}
                words={content.immersion.words || []}
                subliminalKeywords={keywords}
                subliminalPhrases={phrases}
                subliminalAudioUrl={content.immersion.subliminal_audio_url || null}
                visual={content.immersion.visual || null}
                musicArea={content.immersion.music_area || "journey_immersion"}
                musicMode={content.immersion.music_mode || "soft"}
                slug={slug}
                onComplete={() => advance("immersion")}
              />
            </motion.div>
          );
        })()}

        {currentStep === "seal" && (
          <motion.div key="seal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}>
            <CompletionSeal onContinue={() => advance("seal")} />
          </motion.div>
        )}

        {currentStep === "milestone" && (
          <motion.div key="milestone" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}>
            <ShareMoment
              slug={slug}
              day={dayNumber}
              onContinue={() => advance("milestone")}
            />
          </motion.div>
        )}

        {currentStep === "night" && (
          <motion.div
            key="night"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
            style={{ background: P.bgWash }}
          >
            <Check size={28} style={{ color: P.accentGold }} />
            <p
              className="text-[26px] mt-5 text-center italic max-w-md"
              style={{ color: P.textPrimary, fontFamily: JOURNEY_FONT_HEAD, fontWeight: 400 }}
            >
              Day {dayNumber} is in you.
            </p>
            <p
              className="text-[15px] mt-3 text-center max-w-sm"
              style={{ color: P.textSecondary, fontFamily: JOURNEY_FONT_HEAD, fontStyle: "italic" }}
            >
              {content.night_audio?.script_outline ||
                "Tonight, while you rest, what you saw today softens into knowing."}
            </p>
            <button
              onClick={async () => {
                await markStep("night");
                navigate(`/journeys`);
              }}
              className="mt-12 rounded-full px-7 py-3 flex items-center gap-2"
              style={{
                background: `linear-gradient(135deg, ${P.accentGold} 0%, #8E7349 100%)`,
                color: "#FAF7F0",
                fontFamily: JOURNEY_FONT_HEAD,
                fontSize: 14,
                letterSpacing: "0.02em",
                border: "1px solid rgba(168,137,92,0.35)",
              }}
              data-testid="day-complete-btn"
            >
              Mark day complete
            </button>
            <p
              className="mt-6 text-[11px] tracking-[0.3em] uppercase max-w-xs text-center"
              style={{ color: P.textMuted, fontFamily: JOURNEY_FONT_LABEL }}
            >
              tonight, optional sleep audio reinforcement is available in your night sessions
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always-visible exit (top-left), faint. Hidden during the
          uninterrupted absorption tunnel (imprint → cards → expand →
          rapid → immersion → seal → milestone) so focus isn't broken.
          Visible only on arrival + night. */}
      {!["imprint", "flash", "precision", "scenario", "expand", "rapid", "immersion", "seal", "milestone"].includes(currentStep) && (
        <button
          onClick={() => navigate(`/journeys`)}
          className="fixed top-4 left-4 z-50 p-2 rounded-full"
          style={{
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.10)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}
          data-testid="day-back-btn"
          aria-label="Back to journey"
        >
          <ArrowLeft size={16} style={{ color: "rgba(255,255,255,0.85)" }} />
        </button>
      )}
    </div>
  );
}
